// The recipient list on a multi-recipient form: one row per recipient, directly under the amount.
// The rows add up to the total, and an ask preset or a total typed into Other splits evenly back
// across them (see RecipientAllocation for the rules).
//
// Rows are plain markup owned by this view, not subviews. Typing updates values in place so
// focus and cursor position survive; a row re-renders only when its state changes (removed,
// added, stepped), and keeps focus on the matching control when it does.

import Backbone from 'lib/backbone';
import invokeCallbacks from 'app/NVTagCallbacks';
import FieldView from 'app/views/FieldView';
import RecipientAllocation from 'app/models/RecipientAllocation';
import { accounting } from 'lib/accounting';
import currency from 'app/views/helpers/currency';

var _ = Backbone._;
var $ = Backbone.$;

// Search, Add all and Remove all appear above this many recipients.
var COMPACT_ABOVE = 6;

function initials(name) {
  var words = (name || '').trim().split(/\s+/);
  if (!words[0]) {
    return '';
  }
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
}

export default FieldView.extend({
  __name__: 'RecipientListView',
  type: 'recipient_list',
  tagName: 'div',
  className: 'at-recipient-list form-item',
  events: {
    'click [data-action]': 'onRowAction',
    'input .at-recipient-amount-input input': 'onTypeAmount',
    'change .at-recipient-amount-input input': 'onChangeAmount',
    'keydown .at-recipient-amount-input input': 'onAmountKeydown',
    'input [data-recipients="search"]': 'onSearch',
    'click [data-recipients="clear"]': 'onClearSearch',
    'click [data-recipients="add-all"]': 'onAddAll',
    'click [data-recipients="remove-all"]': 'onRemoveAll'
  },
  initialize: function () {
    FieldView.prototype.initialize.call(this);
    _.bindAll(this, 'onAmountChosen', 'pushTotal', 'renderRows', 'updateSubmitState');

    var formview = this.options.formview;
    this.contributionAmountModel = formview.contributionAmountModel;
    // The allocation lives on the form view so that it survives a re-render of the fieldset,
    // which creates a new instance of this view.
    this.allocation = formview.recipientAllocation ||
      (formview.recipientAllocation = new RecipientAllocation(this.def.beneficiaries, this.contributionAmountModel.get('baseAmount')));
    // The submit response fills the rows through the current instance (NVTagView, on a return to the form)
    formview.recipientListView = this;
    this.searchText = '';
    // Row edits move the total on every keystroke; cover costs and the Host's amount callbacks
    // follow the total, so tell them once the donor pauses.
    this.pushTotalSoon = _.debounce(this.pushTotal, 300);

    this.listenTo(currency, 'change', this.renderRows);
    this.listenTo(formview, 'renderStep', this.updateSubmitState);
  },
  render: function () {
    var count = this.allocation.rows.length;
    this.$el.html(this.template({
      compact: count > COMPACT_ABOVE,
      searchPlaceholder: this.resources.fill(this.resources.PrimaryResources.RecipientsSearchPlaceholder, count)
    }));
    this.$list = this.$('[data-recipients="list"]');
    this.$announce = this.$('[data-recipients="announce"]');
    this.searchText = '';
    this.bindAmountView();
    this.renderRows();
    // The form's buttons may render after this fieldset
    _.defer(this.updateSubmitState);
    return this;
  },
  // The amount view is a sibling in the same fieldset; a re-render of the fieldset replaces it.
  bindAmountView: function () {
    var amountView = this.options.parent && this.options.parent.subviews ? this.options.parent.subviews.SelectAmount : null;
    if (amountView === this.amountView) {
      return;
    }
    if (this.amountView) {
      this.stopListening(this.amountView);
    }
    this.amountView = amountView || null;
    if (this.amountView) {
      this.listenTo(this.amountView, 'amountChosen', this.onAmountChosen);
    }
  },
  rowContext: function (row) {
    var resources = this.resources;
    var strings = resources.PrimaryResources;
    var name = row.displayName;
    var amount = this.allocation.amount(row.id);
    return {
      id: row.id,
      displayName: name,
      descriptor: row.descriptor,
      imageUrl: row.imageUrl,
      initials: initials(name),
      removed: this.allocation.isRemoved(row),
      locked: row.locked,
      closed: row.closed,
      message: row.message,
      amountText: accounting.toFixed(amount, 2),
      // Always two decimals, like the amount inputs beside it (currency.format drops ".00")
      amountDisplay: currency.getCurrency().symbol + accounting.toFixed(amount, 2),
      currencySymbol: currency.getCurrency().symbol,
      inputName: 'TandemAmount_' + row.id,
      addLabel: resources.fill(strings.RecipientAddLabel, name),
      removeLabel: resources.fill(strings.RecipientRemoveLabel, name),
      amountLabel: resources.fill(strings.RecipientAmountLabel, name),
      increaseLabel: resources.fill(strings.RecipientIncreaseLabel, name),
      decreaseLabel: resources.fill(strings.RecipientDecreaseLabel, name)
    };
  },
  rowHtml: function (row) {
    return this.options.templates.recipient_row(this.rowContext(row));
  },
  renderRows: function () {
    if (!this.$list) {
      return this;
    }
    this.$list.html(this.allocation.rows.map(this.rowHtml, this).join(''));
    this.applySearch();
    this.updateSummary();
    return this;
  },
  $row: function (id) {
    return this.$list.children('[data-recipient-id="' + id + '"]');
  },
  // Re-renders one row. If focus was inside it, focus moves to the same control in the new row,
  // or to `fallbackAction` when that control is gone (an amount stepped to zero becomes "+ Add").
  renderRow: function (id, fallbackAction) {
    var $old = this.$row(id);
    var active = document.activeElement;
    var hadFocus = $old.length && $.contains($old[0], active);
    var focusAction = hadFocus ? (active.getAttribute('data-action') || (active.tagName === 'INPUT' ? 'input' : null)) : null;

    var $new = $(this.rowHtml(this.allocation.row(id)).trim());
    $old.replaceWith($new);
    this.applySearch();

    if (hadFocus) {
      var selector = function (action) {
        return action === 'input' ? '.at-recipient-amount-input input' : '[data-action="' + action + '"]';
      };
      var $target = $new.find(selector(focusAction));
      if (!$target.length && fallbackAction) {
        $target = $new.find(selector(fallbackAction));
      }
      $target.trigger('focus');
    }
  },
  rowIdFromEvent: function (e) {
    return parseInt($(e.currentTarget).closest('[data-recipient-id]').attr('data-recipient-id'), 10);
  },
  onRowAction: function (e) {
    var id = this.rowIdFromEvent(e);
    var row = this.allocation.row(id);
    if (!row || row.locked) {
      return;
    }
    var action = $(e.currentTarget).attr('data-action');
    switch (action) {
      case 'increase':
      case 'decrease':
        this.stepRow(id, action === 'increase' ? 1 : -1, $(e.currentTarget).closest('.at-recipient-amount-input').find('input')[0]);
        return;
      case 'remove':
        this.allocation.remove(id);
        break;
      case 'add':
        this.allocation.restore(id);
        break;
    }
    var removed = this.allocation.isRemoved(row);
    this.renderRow(id, removed ? 'add' : (action === 'add' ? 'input' : action));
    this.rowsChanged();
    this.announceRow(row, action === 'add');
  },
  // The in-box arrows and the arrow keys. Like a number input, focus stays in the box; a row
  // stepped to zero becomes "+ Add" and focus moves there.
  stepRow: function (id, direction, input) {
    var row = this.allocation.row(id);
    this.allocation.step(id, direction);
    if (this.allocation.isRemoved(row)) {
      input.focus();
      this.renderRow(id, 'add');
    } else {
      input.value = accounting.toFixed(this.allocation.amount(id), 2);
      input.focus();
    }
    this.rowsChanged();
    this.announceRow(row);
  },
  onAmountKeydown: function (e) {
    var key = e.key || e.keyCode;
    var direction = (key === 'ArrowUp' || key === 'Up' || key === 38) ? 1 :
      (key === 'ArrowDown' || key === 'Down' || key === 40) ? -1 : 0;
    if (direction) {
      e.preventDefault();
      this.stepRow(this.rowIdFromEvent(e), direction, e.currentTarget);
    }
  },
  // Typing updates the row and the total without re-rendering, so the cursor stays put. A row
  // typed down to zero still shows its input until the donor leaves it.
  onTypeAmount: function (e) {
    var id = this.rowIdFromEvent(e);
    this.allocation.setAmount(id, accounting.unformat(e.currentTarget.value));
    this.rowsChanged();
  },
  onChangeAmount: function (e) {
    var id = this.rowIdFromEvent(e);
    var row = this.allocation.row(id);
    if (!row) {
      return;
    }
    this.allocation.setAmount(id, accounting.unformat(e.currentTarget.value));
    if (this.allocation.isRemoved(row)) {
      this.renderRow(id, 'add');
    } else {
      e.currentTarget.value = accounting.toFixed(this.allocation.amount(id), 2);
    }
    this.rowsChanged();
    this.announceRow(row);
  },
  // An ask preset or a total typed into Other.
  onAmountChosen: function (amount) {
    this.allocation.splitEvenly(amount);
    this.renderRows();
    this.touch();
    this.announce(this.resources.fill(this.resources.PrimaryResources.RecipientsSplitAnnouncement,
      currency.format(this.allocation.total()), this.allocation.fundedCount()));
  },
  rowMatchesSearch: function (row) {
    var text = this.searchText.trim().toLowerCase();
    return !text ||
      row.displayName.toLowerCase().indexOf(text) !== -1 ||
      row.descriptor.toLowerCase().indexOf(text) !== -1;
  },
  // Search only hides rows. A hidden recipient that's funded still counts toward the total.
  applySearch: function () {
    var shown = 0;
    this.allocation.rows.forEach(function (row) {
      var matches = this.rowMatchesSearch(row);
      this.$row(row.id).toggleClass('at-hide', !matches);
      shown += matches ? 1 : 0;
    }, this);
    this.$('[data-recipients="empty"]').toggleClass('at-hide', shown > 0);
    this.$('[data-recipients="clear"]').toggleClass('at-hide', !this.searchText);
  },
  shownIds: function () {
    return _.map(this.allocation.rows.filter(this.rowMatchesSearch, this), 'id');
  },
  onSearch: function (e) {
    this.searchText = e.currentTarget.value || '';
    this.applySearch();
    this.updateSummary();
  },
  onClearSearch: function () {
    this.searchText = '';
    this.$('[data-recipients="search"]').val('').trigger('focus');
    this.applySearch();
    this.updateSummary();
  },
  // Add all and Remove all act on the rows the current search shows.
  onAddAll: function () {
    this.allocation.restoreAll(this.shownIds());
    this.bulkChanged();
  },
  onRemoveAll: function () {
    this.allocation.removeAll(this.shownIds());
    this.bulkChanged();
  },
  bulkChanged: function () {
    this.renderRows();
    this.rowsChanged();
    this.announce(this.resources.fill(this.resources.PrimaryResources.RecipientsBulkAnnouncement,
      this.allocation.fundedCount(), this.allocation.rows.length, currency.format(this.allocation.total())));
  },
  rowsChanged: function () {
    this.touch();
    this.updateSummary();
    this.pushTotalSoon();
  },
  // Shows the rows' sum as the form's amount, which also moves cover costs and the submit label.
  pushTotal: function () {
    var total = this.allocation.total();
    if (this.amountView) {
      this.amountView.showAmount(total);
    } else {
      this.contributionAmountModel.setBaseAmount(total);
    }
    this.renderFeedback();
  },
  updateSummary: function () {
    var strings = this.resources.PrimaryResources;
    var funded = this.allocation.fundedCount();
    var count = this.allocation.rows.length;
    this.$('[data-recipients="total"]').text(currency.format(this.allocation.total()));
    this.$('[data-recipients="split"]').text(funded ? (this.allocation.isEvenSplit() ? strings.RecipientsEvenSplit : strings.RecipientsCustomSplit) : '');
    this.$('[data-recipients="total-count"]').text(this.resources.fill(strings.RecipientsTotalCount, funded));
    this.$('[data-recipients="count"]').text(this.searchText ?
      this.resources.fill(strings.RecipientsShownCount, this.shownIds().length, count) :
      this.resources.fill(strings.RecipientsSelectedCount, funded, count));
    this.updateSubmitState();
  },
  // A form with no recipient selected can't submit: Contribute, and Next on the list's step, stay
  // disabled until a row has an amount. Locked rows have already gone through, so they don't count.
  // A total under the minimum still submits and shows the amount error.
  updateSubmitState: function () {
    var formview = this.options.formview;
    var noneSelected = this.allocation.fundedCount() === 0;
    var onListStep = !formview.multistep || _.includes(formview.step('view') || [], this.options.parent);
    formview.$('.at-submit, .submitStep').prop('disabled', noneSelected);
    formview.$('.nextStep')
      .toggleClass('disabled', noneSelected && onListStep)
      .attr('aria-disabled', noneSelected && onListStep ? 'true' : null);
  },
  announceRow: function (row, added) {
    var strings = this.resources.PrimaryResources;
    var total = currency.format(this.allocation.total());
    var amount = currency.format(this.allocation.amount(row.id));
    if (this.allocation.isRemoved(row)) {
      this.announce(this.resources.fill(strings.RecipientRemovedAnnouncement, row.displayName, total));
    } else if (added) {
      this.announce(this.resources.fill(strings.RecipientAddedAnnouncement, row.displayName, amount, total));
    } else {
      this.announce(this.resources.fill(strings.RecipientAmountAnnouncement, row.displayName, amount, total));
    }
  },
  announce: function (text) {
    if (this.$announce) {
      this.$announce.text(text);
    }
  },
  // The return to the form after some recipients failed: rows take their amounts, selection,
  // locks and messages from the response's legs.
  applyLegs: function (legs) {
    this.allocation.applyLegs(legs);
    this.renderRows();
    this.pushTotal();
    this.options.formview.invalidateFieldCache();
    return this;
  },
  // Nothing to charge, a total under the composed minimum, or a total over the form's maximum.
  // These are the main form's messages; no amount is ever lowered for the donor.
  errors: function () {
    // Submitting runs errors() before val(), so a row edited in the last moment reaches the amount
    // view, cover costs and the post's Amount before they're checked or read
    this.pushTotalSoon.flush();
    var strings = this.resources.PrimaryResources;
    var activeDef = this.amountView ? this.amountView.getActiveDefinition() : {};
    var total = this.allocation.total();
    var min = this.allocation.minimum(activeDef.valueMin || 0.01);
    var max = parseFloat(activeDef.valueMax);
    var errors = [];
    // After a return to the form where every remaining recipient was removed, there's nothing left to give:
    // Contribute is disabled, and an amount error would only read as a mistake
    var nothingLeft = this.allocation.fundedCount() === 0 && this.allocation.rows.some(function (r) { return r.locked; });
    if (nothingLeft) {
      // no amount error
    } else if (total < min) {
      errors.push(this.resources.fill(strings.InvalidAmountMin, currency.format(min)));
    } else if (max && total > max) {
      errors.push(this.resources.fill(strings.InvalidAmountMax, currency.format(max)));
    }
    errors = invokeCallbacks('alterErrors', {
      val: total,
      field_name: this.field_name(),
      errors: errors,
      def: this.def
    }).errors;
    this.trigger(errors.length ? 'invalid' : 'valid');
    return _.map(errors, _.bind(this.toError, this));
  },
  // Posts `SplitAmounts`, keyed by each funded recipient's gateway account id. The server sums
  // them into the gift total. Removed and locked recipients post nothing.
  val: function () {
    this.pushTotalSoon.flush();
    return { SplitAmounts: this.allocation.toSplitAmounts(this.contributionAmountModel.get('coverCost')) };
  }
});
